const BACKEND_ALIASES = {
  vocodeAlpha: {
    host: "https://fictive-v3.engine.fictivereality.com",
    channel: "alpha",
    family: "vocode",
  },
  vocodeBeta: {
    host: "https://fictive-v3.engine.fictivereality.com",
    channel: "beta",
    family: "vocode",
  },
  vocodeAlphaBackup: {
    host: "https://fictive-v3-backup.engine.fictivereality.com",
    channel: "alpha",
    family: "vocode",
  },
  vocodeBetaBackup: {
    host: "https://fictive-v3-backup.engine.fictivereality.com",
    channel: "beta",
    family: "vocode",
  },
  pipecatDev: {
    host: "https://dev.ai.fictivereality.com",
    channel: "main",
    family: "pipecat",
  },
  pipecatDevAlpha: {
    host: "https://dev.ai.fictivereality.com",
    channel: "alpha",
    family: "pipecat",
  },
  pipecatAlpha: {
    host: "https://prod.ai.fictivereality.com",
    channel: "alpha",
    family: "pipecat",
  },
  pipecatBeta: {
    host: "https://prod.ai.fictivereality.com",
    channel: "beta",
    family: "pipecat",
  },
  pipecatStable: {
    host: "https://prod.ai.fictivereality.com",
    channel: "stable",
    family: "pipecat",
  },
};

export const legacyDialogueAppChannelToAlias = channel => {
  switch (channel) {
    case "alpha":
      return "vocodeAlpha";
    case "beta":
      return "vocodeBeta";
    case "betaBackup":
      return "vocodeBetaBackup";
    case "release":
      return "default";
    default:
      return null;
  }
};

export const normalizeBackendAlias = backendChannel => {
  switch (backendChannel) {
    case "alpha":
      return "vocodeAlpha";
    case "beta":
      return "vocodeBeta";
    case "betaBackup":
      return "vocodeBetaBackup";
    case "dev":
      return "default";
    default:
      return backendChannel || null;
  }
};

export const resolveDefaultBackendAlias = ({ project, frontendChannel }) => {
  if (project === "fictive-dev") {
    return {
      type: "alias",
      alias: "pipecatDev",
    };
  }

  // The beta frontend deliberately uses the stable backend; pipecatBeta stays selectable via
  // an explicit backendChannel setting.
  if (frontendChannel === "beta" || frontendChannel === "stable") {
    return {
      type: "alias",
      alias: "pipecatStable",
    };
  }

  return {
    type: "alias",
    alias: "pipecatAlpha",
  };
};

export const applyBackupAlias = alias => {
  if (alias === "vocodeAlpha") return "vocodeAlphaBackup";
  if (alias === "vocodeBeta") return "vocodeBetaBackup";
  return alias;
};

export const buildAliasBaseUrl = ({ host, family, channel }) => {
  if (family === "pipecat") {
    return `${host}/convai-${channel}`;
  }
  return `${host}/convai_vocode_${channel}`;
};

export const resolveBackendBaseUrl = ({
  project,
  frontendChannel,
  requestedBackendChannel,
  customServerUrl,
  legacyDialogueAppChannel,
  backup = false,
  localBackendUrl = null,
} = {}) => {
  let resolvedAlias = normalizeBackendAlias(requestedBackendChannel);
  let resolvedBaseUrl;
  let resolutionReason = "resolved from backendChannel";

  if (localBackendUrl) {
    resolvedAlias = "localhost";
    resolutionReason = "VITE_LOCAL_BACKEND_URL is set";
    resolvedBaseUrl = localBackendUrl;
  } else if (resolvedAlias === "custom" && customServerUrl) {
    resolvedAlias = "custom";
    resolutionReason = "backendChannel is custom and customServerUrl is set";
    resolvedBaseUrl = customServerUrl;
  } else {
    const legacyAlias = legacyDialogueAppChannelToAlias(legacyDialogueAppChannel);
    const defaultResolution = resolveDefaultBackendAlias({ project, frontendChannel });
    let aliasResolution;

    if (resolvedAlias && resolvedAlias !== "default" && BACKEND_ALIASES[resolvedAlias]) {
      resolutionReason = "resolved from backendChannel";
      aliasResolution = {
        type: "alias",
        alias: resolvedAlias,
      };
    } else if (!resolvedAlias && legacyAlias && BACKEND_ALIASES[legacyAlias]) {
      resolutionReason = "resolved from legacy dialogueAppChannel";
      aliasResolution = {
        type: "alias",
        alias: legacyAlias,
      };
    } else {
      resolutionReason = "resolved from frontend/project default";
      aliasResolution = defaultResolution;
    }

    if (aliasResolution.type === "fixed") {
      resolvedAlias = "defaultFixed";
      resolvedBaseUrl = aliasResolution.baseUrl;
    } else {
      const alias = backup ? applyBackupAlias(aliasResolution.alias) : aliasResolution.alias;
      const target = BACKEND_ALIASES[alias];
      if (!target) {
        throw new Error(`Unknown backend alias: ${alias}`);
      }
      resolvedAlias = alias;
      if (backup && alias !== aliasResolution.alias) {
        resolutionReason += " with backup override";
      }
      resolvedBaseUrl = buildAliasBaseUrl(target);
    }
  }

  return {
    alias: resolvedAlias,
    baseUrl: resolvedBaseUrl,
    reason: resolutionReason,
  };
};
